import {
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormControlProps,
  Stack,
  Switch,
  SwitchProps,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { type ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { useWatchConditions } from 'src/components/Forms/use-validations';
import { isStringBooleanTrue } from 'src/utils/strings';
import { toSxArray } from 'src/utils/sx';

export type FormSwitchProps = Omit<
  FormControlProps,
  'required' | 'defaultValue' | 'options' | 'name'
> & {
  label: ReactNode;
  required?: boolean | string;
  switchFieldProps?: Partial<SwitchProps>;
  defaultValue?: boolean;
  value?: string;
  labelPlacement?: FormControlLabelProps['labelPlacement'];
  labelSx?: SxProps<Theme>;
  trueLabel?: string;
  falseLabel?: string;
  rules?: Parameters<typeof Controller>[0]['rules'];
} & CommonFormFieldProps<'switch', boolean>;

// we need to emulate disabled styles for label and avoid using hoisted disabled attribute
// because disabled checkbox always has `undefined` value in `react-hook-form`
const disabledLabelStyles = {
  pointerEvents: 'none',
  opacity: 0.5,
  cursor: 'not-allowed',
};

export const FormSwitch = (props: FormSwitchProps) => {
  const {
    name,
    label,
    value,
    required,
    switchFieldProps,
    defaultValue,
    showsIf = [],
    labelPlacement,
    trueLabel,
    falseLabel,
    onChange,
    labelSx,
    rules,
    disabled,
    ...formControlProps
  } = props;

  const isDisabled = Boolean(disabled);

  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  const shouldShow = useWatchConditions({
    control,
    showsIf,
  });

  if (!shouldShow) {
    return;
  }

  return (
    <FormControl
      {...formControlProps}
      error={hasErrorMsg}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : formControlProps.tabIndex}
    >
      <FormControlLabel
        labelPlacement={labelPlacement}
        aria-disabled={isDisabled}
        sx={[
          {
            gap: 1,
            m: labelPlacement === 'top' ? 0 : undefined,
            alignItems: labelPlacement === 'top' ? 'start' : 'center',
          },
          ...toSxArray(labelSx),
          ...(isDisabled ? [disabledLabelStyles] : []),
        ]}
        control={
          <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={defaultValue ?? false}
            render={({ field }) => {
              let resolvedValue = defaultValue ?? false;
              if (typeof field.value === 'boolean') {
                resolvedValue = field.value;
              } else if (typeof field.value === 'string') {
                resolvedValue = isStringBooleanTrue(field.value);
              }

              const {
                sx: customSwitchSx,
                inputProps: customInputProps,
                tabIndex: customTabIndex,
                readOnly: customReadOnly,
                ...forwardedSwitchProps
              } = switchFieldProps ?? {};

              return (
                <Stack direction="row" gap={1} alignItems="center">
                  {!!falseLabel && <Typography>{falseLabel}</Typography>}
                  <Switch
                    inputProps={{
                      'aria-label': 'toggle switch',
                      ...customInputProps,
                      ...(isDisabled ? { 'aria-disabled': true } : {}),
                    }}
                    {...field}
                    {...forwardedSwitchProps}
                    readOnly={isDisabled || Boolean(customReadOnly)}
                    tabIndex={isDisabled ? -1 : customTabIndex}
                    checked={resolvedValue}
                    onChange={(event, checked) => {
                      if (isDisabled) {
                        event.preventDefault();
                        return;
                      }
                      field.onChange(checked);
                      onChange?.(event);
                    }}
                    sx={[
                      ...toSxArray(customSwitchSx),
                      ...(isDisabled
                        ? [{ pointerEvents: 'none', opacity: 0.38, cursor: 'not-allowed' }]
                        : []),
                    ]}
                  />
                  {!!trueLabel && <Typography>{trueLabel}</Typography>}
                </Stack>
              );
            }}
          />
        }
        label={label}
      />
    </FormControl>
  );
};
